
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, MessageCircle, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  const fullName = user ? `${user.firstName} ${user.lastName}` : '';

  // Determine the correct profile route based on user type
  const getProfileRoute = () => {
    if (!user) return '/profile';
    
    // Check if user is a mentor (has mentor or both profile type)
    if (user.profileType === 'mentor' || user.profileType === 'both') {
      return `/mentor-profile/${user.id}`;
    } else {
      return `/mentee-profile/${user.id}`;
    }
  };

  // Handle notification icon click to mark all as read
  const handleNotificationClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!user?.id) return;
    
    try {
      // Mark all unread notifications as read
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('receiver_id', user.id)
        .eq('read', false);
        
      if (error) {
        console.error('Error marking notifications as read:', error);
        toast.error('Failed to mark notifications as read');
      } else {
        // Update local state immediately
        setUnreadNotifications(0);
        toast.success('All notifications marked as read');
      }
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      toast.error('Failed to mark notifications as read');
    }
    
    // Navigate to notifications page
    navigate('/notifications');
  };
  
  // Fetch unread notifications count
  useEffect(() => {
    if (user && user.id) {
      const fetchUnreadCounts = async () => {
        try {
          console.log("=== FETCHING UNREAD COUNTS DEBUG ===");
          console.log("Fetching counts for user:", user.id);

          // Fetch ALL unread notifications count (including new_message type)
          const { count: allNotificationsCount, error: notificationError } = await supabase
            .from('notifications')
            .select('*', { count: 'exact', head: true })
            .eq('receiver_id', user.id)  // FIXED: Using receiver_id consistently
            .eq('read', false);

          if (!notificationError) {
            console.log("✅ All unread notifications count:", allNotificationsCount);
            setUnreadNotifications(allNotificationsCount || 0);
          } else {
            console.error("Error fetching notifications:", notificationError);
          }

          // Fetch unread messages count from conversations
          const { data: conversationsData, error: conversationsError } = await supabase
            .from('conversations')
            .select('id')
            .or(`mentor_id.eq.${user.id},mentee_id.eq.${user.id}`);

          if (!conversationsError && conversationsData) {
            if (conversationsData.length > 0) {
              const conversationIds = conversationsData.map(conv => conv.id);
              
              const { count: messageCount, error: messageError } = await supabase
                .from('messages')
                .select('*', { count: 'exact', head: true })
                .in('conversation_id', conversationIds)
                .eq('is_read', false)
                .neq('sender_id', user.id);

              if (!messageError) {
                console.log("✅ Unread messages count:", messageCount);
                setUnreadMessages(messageCount || 0);
              }
            } else {
              setUnreadMessages(0);
            }
          }
          
          console.log("=== END FETCHING UNREAD COUNTS DEBUG ===");
        } catch (error) {
          console.error("Error fetching notification counts:", error);
        }
      };

      fetchUnreadCounts();
      
      // Subscribe to notification changes for real-time updates
      const notificationChannel = supabase
        .channel('user-menu-notifications')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'notifications',
          filter: `receiver_id=eq.${user.id}`  // FIXED: Using receiver_id consistently
        }, (payload) => {
          console.log('UserMenu - Notification change:', payload);
          fetchUnreadCounts();
          
          // Show toast for new notifications
          if (payload.eventType === 'INSERT') {
            const newNotification = payload.new as any;
            if (newNotification.type === 'new_message') {
              console.log("🔔 UserMenu - New message notification received");
              toast.info("New message received", {
                description: newNotification.content,
              });
            }
          }
        })
        .subscribe();

      // Subscribe to message changes for real-time unread count updates
      const messageChannel = supabase
        .channel('user-menu-messages')
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        }, (payload) => {
          const newMessage = payload.new as any;
          // Only update count if the message is not from the current user
          if (newMessage.sender_id !== user.id) {
            fetchUnreadCounts();
          }
        })
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages'
        }, () => {
          // Update counts when messages are marked as read
          fetchUnreadCounts();
        })
        .subscribe();

      return () => {
        supabase.removeChannel(notificationChannel);
        supabase.removeChannel(messageChannel);
      };
    }
  }, [user]);

  return (
    <div className="flex items-center gap-6">
      {/* Notifications Icon */}
      <div className="relative">
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative hover:glow-white transition-all duration-200"
          onClick={handleNotificationClick}
        >
          <Bell className="h-8 w-8 text-[#FFFFFF]" strokeWidth={2.75} />
          {unreadNotifications > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {unreadNotifications}
            </span>
          )}
        </Button>
      </div>

      {/* Messages Icon */}
      <Link to="/messages" className="relative">
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative hover:glow-white transition-all duration-200"
        >
          <MessageCircle className="h-8 w-8 text-[#FFFFFF]" strokeWidth={2.75} />
          {unreadMessages > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {unreadMessages}
            </span>
          )}
        </Button>
      </Link>

      {/* User Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            className="relative h-10 w-10 rounded-full hover:glow-white transition-all duration-200"
          >
            <Avatar className="h-10 w-10 border-2 border-white">
              {user && user.avatar_url ? (
                <AvatarImage 
                  src={user.avatar_url} 
                  alt={fullName}
                />
              ) : null}
              <AvatarFallback>
                {fullName ? getInitials(fullName) : <User />}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end">
          <div className="flex items-center justify-start gap-2 p-2">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{fullName}</p>
              <p className="text-xs leading-none text-muted-foreground">
                {user?.email}
              </p>
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to={getProfileRoute()} className="cursor-pointer">Profile</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/account" className="cursor-pointer">Account</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/notifications" className="cursor-pointer">
              Notifications
              {unreadNotifications > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                  {unreadNotifications}
                </span>
              )}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/messages" className="cursor-pointer">
              Messages
              {unreadMessages > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                  {unreadMessages}
                </span>
              )}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            className="cursor-pointer text-red-500 focus:text-red-500"
            onClick={logout}
          >
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default UserMenu;
